import { Permission } from '@pique/graphql';
import { useI18n } from '@pique/i18n';
import { useCallback, useEffect, useState } from 'react';

import { Input } from '../../ui/input';
import { ConfirmModal } from '../../ui/modal';

// Email validation regex
const emailRegex =
  /^(?:(?:[^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(?:(?:\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|((?:[a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export interface InviteModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onConfirm: (params: { email: string; permission: Permission }) => void;
  isMutating: boolean;
}

export const InviteModal = ({
  open,
  setOpen,
  onConfirm,
  isMutating,
}: InviteModalProps) => {
  const t = useI18n();
  const [inviteEmail, setInviteEmail] = useState('');
  const [permission] = useState(Permission.Write);
  const [isValidEmail, setIsValidEmail] = useState(true);

  const handleConfirm = useCallback(() => {
    if (!emailRegex.test(inviteEmail)) {
      setIsValidEmail(false);
      return;
    }
    setIsValidEmail(true);

    onConfirm({
      email: inviteEmail,
      permission,
    });
  }, [inviteEmail, onConfirm, permission]);

  useEffect(() => {
    if (!open) {
      setInviteEmail('');
      setIsValidEmail(true);
    }
  }, [open]);

  return (
    <ConfirmModal
      open={open}
      onOpenChange={setOpen}
      title={t['Invite Members']()}
      description={t['Invite Members Message']()}
      cancelText={t['com.pique.inviteModal.button.cancel']()}
      contentOptions={{
        ['data-testid' as string]: 'invite-modal',
        style: {
          padding: '20px 26px',
        },
      }}
      confirmText={t['Invite']()}
      confirmButtonOptions={{
        loading: isMutating,
        variant: 'primary',
        ['data-testid' as string]: 'confirm-enable-affine-cloud-button',
      }}
      onConfirm={handleConfirm}
    >
      <div>
        <Input
          disabled={isMutating}
          placeholder="email@example.com"
          value={inviteEmail}
          onChange={setInviteEmail}
          status={!isValidEmail ? 'error' : 'default'}
          onEnter={handleConfirm}
          size="large"
        />
        {!isValidEmail && (
          <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
            {t['com.pique.auth.sign.email.error']()}
          </div>
        )}
      </div>
    </ConfirmModal>
  );
};
